import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supportApi } from '@/api/support';
import { ArrowLeft, Lifebuoy } from '@/components/icons';
import { PageHeader, PageShell } from '@/components/system';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { SupportPage } from '@/pages/constants';
import { createPageUrl } from '@/utils';
import { SupportThread } from './components/SupportThread';

export default function SupportTicketPage() {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const queryKey = ['support', 'ticket', ticketId];
    const {
        data: ticket,
        isLoading,
        isError,
    } = useQuery({
        queryKey,
        queryFn: () => supportApi.getTicket(ticketId as string),
        enabled: Boolean(ticketId),
        refetchInterval: 5_000,
    });

    const sendMessageMutation = useMutation({
        mutationFn: (body: string) => supportApi.addTicketMessage(ticketId as string, body),
        onSuccess: (updatedTicket) => {
            queryClient.setQueryData(queryKey, updatedTicket);
            queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] });
        },
        onError: () => {
            toast.error('Não foi possível enviar a resposta.');
        },
    });

    const closeTicketMutation = useMutation({
        mutationFn: () => supportApi.closeTicket(ticketId as string),
        onSuccess: (updatedTicket) => {
            queryClient.setQueryData(queryKey, updatedTicket);
            queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] });
            toast.success('Chamado fechado.');
        },
        onError: () => {
            toast.error('Não foi possível fechar o chamado.');
        },
    });

    if (isLoading) {
        return <Loading />;
    }

    if (isError || !ticket) {
        return (
            <PageShell width="narrow">
                <PageHeader
                    eyebrow="Suporte"
                    title="Chamado não encontrado"
                    subtitle="Esse chamado não existe ou não está disponível para sua conta."
                    icon={Lifebuoy}
                    action={
                        <Button onClick={() => navigate(createPageUrl(SupportPage))}>
                            Voltar ao suporte
                        </Button>
                    }
                />
            </PageShell>
        );
    }

    return (
        <PageShell fill width="wide">
            <div className="flex min-h-0 flex-1 overflow-hidden">
                <SupportThread
                    ticket={ticket}
                    perspective="user"
                    isSending={sendMessageMutation.isPending}
                    onSendMessage={(body) => sendMessageMutation.mutateAsync(body)}
                    onCloseTicket={() => closeTicketMutation.mutateAsync()}
                    isClosing={closeTicketMutation.isPending}
                    headerLeading={
                        <Button variant="outline" size="icon" asChild>
                            <Link
                                to={createPageUrl(SupportPage)}
                                aria-label="Meus chamados"
                                title="Meus chamados"
                            >
                                <ArrowLeft className="size-4" />
                            </Link>
                        </Button>
                    }
                />
            </div>
        </PageShell>
    );
}
